import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
// Using custom Bootstrap pagination for consistency
import { getDesignations, saveDesignation } from "../../components/Api/GmsDesignation";
import { getCompanies } from "../../components/Api/GmsCompany";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

const defaultForm = {
  designation_Id: 0,
  designation_NameEn: "",
  designation_NameAr: "",
  designation_Company_Id: null,
  designation_CreatedDate: new Date().toISOString(),
  designation_CreatedBy: 1,
  designation_ModifiedDate: new Date().toISOString(),
  designation_ModifiedBy: 1,
};

const emptyFilter = {
  designation_Code: "",
  designation_NameEn: "",
  designation_NameAr: "",
  designation_Company_Id: null,
};

export default function DesignationPage() {
  const [designations, setDesignations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Code",
        accessorKey: "designation_Code",
        enableColumnFilter: false,
      },
      {
        header: "Name (En)",
        accessorKey: "designation_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Name (Ar)",
        accessorKey: "designation_NameAr",
        enableColumnFilter: false,
      },
      {
        header: "Group",
        accessorKey: "company_NameEn",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const designation = row.original;
          const company = companies.find(c => c.company_Id === designation.designation_Company_Id);
          return company ? company.company_NameEn : "N/A";
        },
      },
      {
        header: "View",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleView(row.original);
            }}
            className="btn btn-link p-0"
            disabled={viewLoadingRow === row.original.designation_Id}
          >
            {viewLoadingRow === row.original.designation_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <Eye size={20} color="black" />
            )}
          </button>
        ),
      },
      {
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.original);
            }}
            className="btn btn-link p-0"
            disabled={editLoadingRow === row.original.designation_Id}
          >
            {editLoadingRow === row.original.designation_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <SquarePen size={20} color="black" />
            )}
          </button>
        ),
      },
    ],
    [viewLoadingRow, editLoadingRow, companies]
  );

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadDesignations();
      loadCompanies();
    }
  }, [permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsDesignation');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsDesignation');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsDesignation');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadDesignations = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getDesignations(filterData);
      setDesignations(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load designations: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await getCompanies({});
      setCompanies(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load companies: " + error.message, "error");
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        designation_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value?.value || value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await saveDesignation({
        ...form,
        designation_Id: form.designation_Id === 0 ? null : form.designation_Id,
        designation_ModifiedDate: new Date().toISOString(),
      });
      Swal.fire("Success", result.message || "Designation saved successfully", "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadDesignations(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save designation: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (designation) => {
    setViewLoadingRow(designation.designation_Id);
    try {
      const company = companies.find(c => c.company_Id === designation.designation_Company_Id);
      const companyName = company ? company.company_NameEn : "N/A";

      Swal.fire({
        title: designation.designation_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Name (English):</strong> ${designation.designation_NameEn}</p>
              <p><strong>Name (Arabic):</strong> ${designation.designation_NameAr}</p>
              <p><strong>Company:</strong> ${companyName}</p>
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Timestamps</h6>
              <p><strong>Created Date:</strong> ${new Date(designation.designation_CreatedDate).toLocaleString()}</p>
              <p><strong>Last Modified:</strong> ${new Date(designation.designation_ModifiedDate).toLocaleString()}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view designation details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (designation) => {
    setForm({
      ...designation,
      designation_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadDesignations(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadDesignations(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedDesignations = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return designations.slice(startIndex, endIndex);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  const companyOptions = companies.map(company => ({
    value: company.company_Id,
    label: company.company_NameEn
  }));

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Designation Management</h4>

                {/* Form Section - Only show if user has edit permission */}
                {canEdit && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        {isEditing ? "Edit Designation" : "Add New Designation"}
                      </h5>
                      <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">Name (English)</Label>
                        <Input
                          type="text"
                          value={form.designation_NameEn}
                          onChange={(e) => handleFormChange("designation_NameEn", e.target.value)}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">Name (Arabic)</Label>
                        <Input
                          type="text"
                          value={form.designation_NameAr}
                          onChange={(e) => handleFormChange("designation_NameAr", e.target.value)}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">Group</Label>
                        <Select
                          key={form.designation_Company_Id || 'empty'}
                          value={companyOptions.find(
                            option => option.value === form.designation_Company_Id
                          )}
                          onChange={(selected) =>
                            handleFormChange("designation_Company_Id", selected?.value)
                          }
                          options={companyOptions}
                          isClearable
                          placeholder="Select Group"
                        />
                      </div>
                    </Col>
                  </Row>
                  <div className="d-flex gap-2">
                    <Button type="submit" color="primary" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Loading...
                        </>
                      ) : isEditing ? (
                        "Update"
                      ) : (
                        "Save"
                      )}
                    </Button>
                    {isEditing && (
                      <Button
                        type="button"
                        color="secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </Form>
              </CardBody>
            </Card>
            )}

            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title">Designations List</h4>
                  <Button
                    color="primary"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </Button>
                </div>

                {showFilters && (
                  <div className="mb-4">
                    <Row>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Code</Label>
                          <Select
                            value={designations
                              .map(d => ({ value: d.designation_Code, label: d.designation_Code }))
                              .find(option => option.value === filter.designation_Code)}
                            onChange={(selected) =>
                              handleFilterChange("designation_Code", selected)
                            }
                            options={designations.map(d => ({
                              value: d.designation_Code,
                              label: d.designation_Code
                            }))}
                            isClearable
                            placeholder="Select Code"
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Name (English)</Label>
                          <Select
                            value={designations
                              .map(d => ({ value: d.designation_NameEn, label: d.designation_NameEn }))
                              .find(option => option.value === filter.designation_NameEn)}
                            onChange={(selected) =>
                              handleFilterChange("designation_NameEn", selected)
                            }
                            options={designations.map(d => ({
                              value: d.designation_NameEn,
                              label: d.designation_NameEn
                            }))}
                            isClearable
                            placeholder="Select Name (En)"
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Name (Arabic)</Label>
                          <Select
                            value={designations
                              .map(d => ({ value: d.designation_NameAr, label: d.designation_NameAr }))
                              .find(option => option.value === filter.designation_NameAr)}
                            onChange={(selected) =>
                              handleFilterChange("designation_NameAr", selected)
                            }
                            options={designations.map(d => ({
                              value: d.designation_NameAr,
                              label: d.designation_NameAr
                            }))}
                            isClearable
                            placeholder="Select Name (Ar)"
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <Label className="form-label">Group</Label>
                          <Select
                            value={companyOptions.find(
                              option => option.value === filter.designation_Company_Id
                            )}
                            onChange={(selected) =>
                              handleFilterChange(
                                "designation_Company_Id",
                                selected
                              )
                            }
                            options={companyOptions}
                            isClearable
                            placeholder="Select Group"
                          />
                        </div>
                      </Col>
                    </Row>
                    <div className="d-flex gap-2">
                      <Button color="primary" onClick={handleApplyFilter}>
                        Apply Filters
                      </Button>
                      <Button color="secondary" onClick={handleClearFilter}>
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                )}

                <div className="table-responsive">
                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th>Sr.No</th>
                        <th>Code</th>
                        <th>Name (EN)</th>
                        <th>Name (AR)</th>
                        <th>Group</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {designations.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No designations found
                          </td>
                        </tr>
                      ) : (
                        getPaginatedDesignations().map((designation, index) => (
                          <tr key={designation.designation_Id}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>{designation.designation_Code}</td>
                            <td>{designation.designation_NameEn}</td>
                            <td>{designation.designation_NameAr}</td>
                            <td>{companies.find(c => c.company_Id === designation.designation_Company_Id)?.company_NameEn || '-'}</td>

                            <td>
                              <div className="d-flex gap-2">
                                {canEdit && (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => handleEdit(designation)}
                                  >
                                    Edit
                                  </Button>
                                )}
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleView(designation)}
                                >
                                  View
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
